/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1712534400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Apr 08 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "display-video",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup via OAuth",
          "depth": 0,
          "id": "setup-via-oauth"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "User consent",
          "depth": 1,
          "id": "user-consent"
        }, {
          "value": "PII data and hashing",
          "depth": 1,
          "id": "pii-data-and-hashing"
        }, {
          "value": "User identifiers",
          "depth": 1,
          "id": "user-identifiers"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      strong: "strong",
      h3: "h3",
      a: "a",
      img: "img",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {PremiumDestination, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Google Display Video 360"
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "With the Google Display & Video 360 integration, Hightouch can sync first and third party audiences directly from your warehouse."
      }), "\n", _jsx(_components.p, {
        children: "This allows you to:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Create ad target groups for your own users, using email, physical address or mobile device ID."
        }), "\n", _jsx(_components.li, {
          children: "Create target groups from users shared with a partner."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "setup-via-oauth",
        children: "Setup via OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Google Display & Video 360"
        }), " destination option. Hightouch will prompt you to authorize our app with your Google account with the following a\nscope to create and update audiences on your DV360 accounts."]
      }), "\n", _jsx(_components.p, {
        children: "After authorizing, you will have successfully connected Hightouch to DV360."
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows to create and update first and third party audiences as customer match user lists. These lists\nsupport adding members using the mobile device ID, or an array of email, phone numbers, and physical addresses."
      }), "\n", _jsx(_components.p, {
        children: "When creating the audience, make sure the source of the data is properly selected, between First or Third party sources."
      }), "\n", _jsx(_components.h3, {
        id: "user-consent",
        children: "User consent"
      }), "\n", _jsxs(_components.p, {
        children: ["Starting in March 2024, Display & Video 360 introduced the ", _jsx(_components.a, {
          href: "https://developers.google.com/display-video/api/guides/audiences/upload-customer-match?sjid=10202302840984618943-NA#set_user_consent",
          children: "consent object"
        }), " for uploading consent with customer match data. The consent object specifies two distinct types of consent and users must grant both types of consent to use their data for customer match lists.  Reference the ", _jsx(_components.a, {
          href: "https://support.google.com/displayvideo/answer/14673239?hl=en",
          children: "DV360 docs"
        }), " for more information."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-display-video-consent.png",
          alt: "Consent status to match lists"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "If you don't set the consent status or if the status is denied, the sync results in an error."
        })
      }), "\n", _jsx(_components.h3, {
        id: "pii-data-and-hashing",
        children: "PII data and hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch will automatically hash all the PII data before it's sent to Google. If the data is already\nhashed, this option can be disabled. Reference the ", _jsx(_components.a, {
          href: "https://developers.google.com/display-video/api/reference/rest/v1/firstAndThirdPartyAudiences#ContactInfo",
          children: "DV360 docs"
        }), " for more information."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Google's Personalized Ads Policy, Customer Match Policy and Platforms program\npolicies include requirements for advertisers to show sufficiently established\nGoogle Ads or Display & Video 360 account history before accessing certain\nfeatures. To enable uploading data to DV360, please fill ", _jsx(_components.a, {
            href: "https://support.google.com/displayvideo/answer/9176010",
            children: "this\nform"
          }), " before you\ncreate the sync."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsx(_components.p, {
        children: "Mobile device IDs and other user identifiers can not be mixed. Also, if providing the user's physical address, the zip code,\ncountry code, first and last name must be provided."
      }), "\n", _jsxs(_components.p, {
        children: ["Phone numbers must be in ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/E.164",
          children: "E.164"
        }), " format."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "can-not-use-an-unknown-or-unspecified-as-enum-value",
        children: "Can not use an UNKNOWN or UNSPECIFIED as enum value"
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure to set the ", _jsx(_components.a, {
          href: "#user-consent",
          children: "consent status"
        }), " because Google rejects any users where the consent status is ", _jsx(_components.code, {
          children: "Unspecified"
        }), " or ", _jsx(_components.code, {
          children: "Denied"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
